// Loginpage.jsx — light theme, matches app UI (Resolut)
import { useState, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import {
  auth,
  googleProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
} from "../firebaseConfig"
import mixpanel from "mixpanel-browser"
import toast, { Toaster } from "react-hot-toast"
import resolutLogo from "../assets/resolut-logo.png"
import AuthLeftPanel from "../components/AuthLeftPanel"
import { useOnboarding } from "../contexts/OnboardingContext"

const FF = '"Plus Jakarta Sans", "DM Sans", -apple-system, BlinkMacSystemFont, sans-serif'

// ── Tokens (light theme, aligned with HomePage / Sidebar / WildTab) ──
const C = {
  bg:         "#FAFAFA",   // page background
  bgLeft:     "#F5F5F5",   // left panel surface
  bgCard:     "#FFFFFF",   // card surfaces
  bgInput:    "#FFFFFF",   // input bg
  bgInputHov: "#FAFAFA",
  border:     "#E4E4E7",   // default border
  borderFoc:  "#18181b",   // focused border
  text:       "#18181b",   // primary text (ink)
  textMuted:  "#52525b",   // secondary
  textDim:    "#71717a",   // tertiary
  textFaint:  "#a1a1aa",   // placeholder / caption
  accent:     "#18181b",   // primary CTA
  accentHov:  "#3f3f46",
  accentText: "#FFFFFF",
  ember:      "#F25A20",   // brand highlight / underline accent
  errorBg:    "#FEF2F2",
  errorBorder:"#FECACA",
  errorInk:   "#B91C1C",
}

/* ── useIsMobile ── */
const useIsMobile = () => {
  const [v, setV] = useState(() => typeof window !== "undefined" && window.innerWidth < 900)
  useEffect(() => {
    const h = () => setV(window.innerWidth < 900)
    window.addEventListener("resize", h)
    return () => window.removeEventListener("resize", h)
  }, [])
  return v
}

/* ── Spinner ── */
const Spinner = ({ color = "#FFFFFF" }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ animation: "ls-spin 0.8s linear infinite" }}>
    <circle cx="12" cy="12" r="10" stroke={color + "44"} strokeWidth="2.5" />
    <path d="M12 2a10 10 0 0110 10" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
  </svg>
)

/* ── Google Icon ── */
const GoogleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
)

const EyeIcon = () => (
  <svg width="15" height="15" viewBox="0 0 20 20" fill="none">
    <path d="M1 10s3.6-6 9-6 9 6 9 6-3.6 6-9 6-9-6-9-6z" stroke="currentColor" strokeWidth="1.4" />
    <circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.4" />
  </svg>
)
const EyeOffIcon = () => (
  <svg width="15" height="15" viewBox="0 0 20 20" fill="none">
    <path d="M3 3l14 14M8.5 8.6A2.5 2.5 0 0012.4 12M6.5 5.6C4.5 6.8 2.8 8.7 1 10c1.6 2 5 6 9 6a8.5 8.5 0 004.5-1.5M9 4.1A8.4 8.4 0 0110 4c4 0 7.4 4 9 6a14 14 0 01-2.1 2.6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
)

/* ── Input ── */
function LightInput({ label, error, icon, right, ...props }) {
  const [focused, setFocused] = useState(false)
  return (
    <div>
      {label && (
        <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: C.text, fontFamily: FF, marginBottom: 7 }}>
          {label}
        </label>
      )}
      <div style={{
        display: "flex", alignItems: "center",
        height: 46,
        background: C.bgInput,
        border: `1px solid ${error ? C.errorBorder : focused ? C.borderFoc : C.border}`,
        borderRadius: 10,
        transition: "border-color 0.15s, box-shadow 0.15s",
        boxShadow: focused ? "0 0 0 3px rgba(24,24,27,0.06)" : "none",
        overflow: "hidden",
        position: "relative",
      }}>
        {icon && (
          <div style={{ width: 42, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: C.textDim }}>
            {icon}
          </div>
        )}
        <input
          style={{
            flex: 1, border: "none", background: "transparent",
            fontSize: 14, fontWeight: 500, color: C.text,
            outline: "none", fontFamily: FF,
            height: "100%",
            paddingLeft: icon ? 0 : 14,
            paddingRight: right ? 44 : 14,
          }}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          {...props}
        />
        {right}
      </div>
      {error && (
        <p style={{ margin: "6px 0 0", fontSize: 12, fontWeight: 500, color: C.errorInk, fontFamily: FF }}>{error}</p>
      )}
    </div>
  )
}

function friendlyError(code) {
  const m = {
    "auth/user-not-found": "No account found with this email.",
    "auth/wrong-password": "Incorrect password. Try again.",
    "auth/invalid-credential": "Incorrect email or password.",
    "auth/invalid-email": "Please enter a valid email address.",
    "auth/too-many-requests": "Too many attempts. Please wait a moment.",
    "auth/popup-closed-by-user": "Google sign-in was cancelled.",
    "auth/network-request-failed": "Network error. Check your connection.",
  }
  return m[code] || "Something went wrong. Please try again."
}

/* ══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════════════════ */
export default function LoginPage() {
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const { loadProfile } = useOnboarding()

  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [showPw, setShowPw] = useState(false)
  const [errors, setErrors] = useState({})
  const [globalErr, setGlobalErr] = useState("")
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)

  const toastStyle = {
    background: C.bgCard,
    color: C.text,
    border: `1px solid ${C.border}`,
    boxShadow: "0 10px 30px rgba(24,24,27,0.08)",
    fontFamily: FF,
    fontSize: 13,
    fontWeight: 500,
  }

  const handleGoogle = async () => {
    setGoogleLoading(true)
    setGlobalErr("")
    try {
      const result = await signInWithPopup(auth, googleProvider)
      const isNew =
        result._tokenResponse?.isNewUser ??
        result.user?.metadata?.creationTime ===
          result.user?.metadata?.lastSignInTime

      if (isNew) {
        await result.user.delete()
        toast("No account found. Redirecting to sign up…", {
          icon: "👋",
          style: toastStyle,
        })
        setTimeout(
          () =>
            navigate("/register", {
              state: { email: result.user.email, fromGoogle: true },
            }),
          1500,
        )
        return
      }

      toast.success("Welcome back!", {
        style: toastStyle,
        iconTheme: { primary: C.accent, secondary: "#fff" },
      })
      mixpanel.track("Signed In", { method: "google" })
      const loaded = await loadProfile(result.user.uid)
      if (loaded && loaded.onboardingComplete === true) {
        navigate("/dashboard/home")
      } else {
        navigate("/register", { state: { step: 3 } })
      }
    } catch (err) {
      if (err.code !== "auth/popup-closed-by-user") {
        const msg = friendlyError(err.code)
        setGlobalErr(msg)
        toast.error(msg, {
          style: { ...toastStyle, border: `1px solid ${C.errorBorder}` },
          iconTheme: { primary: C.errorInk, secondary: "#fff" },
        })
      }
    } finally {
      setGoogleLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e?.preventDefault()
    const err = {}
    if (!email.includes("@")) err.email = "Enter a valid email address"
    if (!password) err.password = "Please enter your password"
    setErrors(err)
    if (Object.keys(err).length > 0) return
    setLoading(true); setGlobalErr("")
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password)
      mixpanel.track("Signed In", { email, method: "email" })
      const loaded = await loadProfile(cred.user.uid)
      if (loaded && loaded.onboardingComplete === true) {
        navigate("/dashboard/home")
      } else {
        navigate("/register", { state: { step: 3 } })
      }
    } catch (err) { setGlobalErr(friendlyError(err.code)) }
    finally { setLoading(false) }
  }

  /* ── Logo ── */
  const Logo = (
    <Link to="/" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
      <img src={resolutLogo} alt="Resolut" style={{ width: 32, height: 32, borderRadius: 7, objectFit: "contain", flexShrink: 0 }} />
      <span style={{ fontSize: 18, fontWeight: 700, letterSpacing: "-0.025em", color: C.text, fontFamily: FF }}>
        Resolut
      </span>
    </Link>
  )

  return (
    <div style={{ minHeight: "100vh", display: "flex", background: C.bg, fontFamily: FF, color: C.text }}>
      <Toaster position="top-center" />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=DM+Sans:wght@400;500;600;700&display=swap');
        @keyframes ls-spin { to { transform: rotate(360deg); } }
        * { box-sizing: border-box; }
        input::placeholder { color: ${C.textFaint}; }
        input:disabled { opacity: 0.5; cursor: not-allowed; }
        .ls-divider-line { flex: 1; height: 1px; background: ${C.border}; }
      `}</style>

      {/* ════ LEFT PANEL (shared with Sign Up) ════ */}
      {!isMobile && <AuthLeftPanel />}

      {/* ════════════════════════════════════════
          RIGHT PANEL — form
      ════════════════════════════════════════ */}
      <div style={{
        flex: 1,
        display: "flex",
        alignItems: isMobile ? "flex-start" : "center",
        justifyContent: "center",
        padding: isMobile ? "32px 24px 48px" : "56px 72px",
        background: C.bg,
      }}>
        <div style={{ width: "100%", maxWidth: 420 }}>

          {/* Mobile logo */}
          {isMobile && <div style={{ marginBottom: 32 }}>{Logo}</div>}

          {/* Heading */}
          <div style={{ marginBottom: 32 }}>
            <h1 style={{
              fontSize: 28, fontWeight: 700, color: C.text,
              margin: "0 0 6px", fontFamily: FF, letterSpacing: "-0.025em",
            }}>
              Welcome back
            </h1>
            <p style={{ fontSize: 14.5, color: C.textMuted, margin: 0, fontFamily: FF, lineHeight: 1.5 }}>
              Sign in to continue to your dashboard.
            </p>
          </div>

          {/* Error banner */}
          {globalErr && (
            <div style={{
              marginBottom: 20, padding: "11px 14px",
              background: C.errorBg,
              border: `1px solid ${C.errorBorder}`,
              borderRadius: 10,
              display: "flex", alignItems: "flex-start", gap: 10,
            }}>
              <svg width="15" height="15" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
                <circle cx="10" cy="10" r="8" stroke={C.errorInk} strokeWidth="1.5" />
                <path d="M10 6v4M10 14h.01" stroke={C.errorInk} strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <span style={{ fontSize: 13, fontWeight: 500, color: C.errorInk, fontFamily: FF }}>{globalErr}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>

            <LightInput
              label="Email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setErrors((p) => ({ ...p, email: "" })) }}
              autoComplete="email"
              error={errors.email}
              required
            />

            <LightInput
              label="Password"
              type={showPw ? "text" : "password"}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setErrors((p) => ({ ...p, password: "" })) }}
              autoComplete="current-password"
              error={errors.password}
              required
              right={
                <button type="button" onClick={() => setShowPw((s) => !s)}
                  style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", padding: 6, display: "flex", alignItems: "center", color: C.textDim, borderRadius: 6 }}
                  onMouseEnter={(e) => e.currentTarget.style.color = C.text}
                  onMouseLeave={(e) => e.currentTarget.style.color = C.textDim}>
                  {showPw ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              }
            />

            {/* Forgot password */}
            <div style={{ textAlign: "right", marginTop: -4 }}>
              <Link to="/forgot-password" style={{
                fontSize: 13, fontWeight: 500, color: C.textMuted, fontFamily: FF,
                textDecoration: "none", transition: "color 0.15s",
              }}
                onMouseEnter={(e) => e.target.style.color = C.text}
                onMouseLeave={(e) => e.target.style.color = C.textMuted}>
                Forgot password?
              </Link>
            </div>

            {/* Primary CTA */}
            <button type="submit" disabled={loading}
              style={{
                width: "100%", height: 46,
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                background: loading ? C.accentHov : C.accent,
                color: C.accentText,
                border: "none", borderRadius: 10,
                fontSize: 14, fontWeight: 600, fontFamily: FF,
                letterSpacing: "0.01em",
                cursor: loading ? "not-allowed" : "pointer",
                transition: "background 0.15s, transform 0.08s",
                marginTop: 4,
              }}
              onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = C.accentHov }}
              onMouseLeave={(e) => { if (!loading) e.currentTarget.style.background = C.accent }}
            >
              {loading ? <Spinner color="#fff" /> : "Sign in"}
            </button>

            {/* Divider */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "4px 0" }}>
              <div className="ls-divider-line" />
              <span style={{ fontSize: 12, fontWeight: 500, color: C.textDim, fontFamily: FF, flexShrink: 0 }}>or</span>
              <div className="ls-divider-line" />
            </div>

            {/* Google */}
            <button type="button" onClick={handleGoogle} disabled={googleLoading}
              style={{
                width: "100%", height: 46,
                display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                background: C.bgCard,
                border: `1px solid ${C.border}`,
                borderRadius: 10,
                fontSize: 14, fontWeight: 500, fontFamily: FF,
                color: C.text,
                cursor: googleLoading ? "not-allowed" : "pointer",
                transition: "border-color 0.15s, background 0.15s",
              }}
              onMouseEnter={(e) => { if (!googleLoading) { e.currentTarget.style.borderColor = C.textDim; e.currentTarget.style.background = "#FAFAFA" } }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = C.bgCard }}
            >
              {googleLoading ? <Spinner color={C.text} /> : <GoogleIcon />}
              {googleLoading ? "Connecting…" : "Continue with Google"}
            </button>

            {/* Register link */}
            <p style={{
              margin: "16px 0 0", fontSize: 13.5, fontWeight: 500,
              color: C.textMuted, fontFamily: FF,
              textAlign: "center",
            }}>
              New to Resolut?{" "}
              <Link to="/register" style={{
                color: C.text, fontWeight: 600,
                textDecoration: "none",
              }}>
                Create an account
              </Link>
            </p>
          </form>

          {/* Mobile footer */}
          {isMobile && (
            <div style={{ display: "flex", gap: 20, marginTop: 40, justifyContent: "center" }}>
              {["Terms", "Privacy"].map((l) => (
                <a key={l} href="#" style={{ fontSize: 12, color: C.textDim, fontFamily: FF, textDecoration: "none" }}>{l}</a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
